import { Button, cn, Spinner, Stack } from '@landler/component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentType, Conversation } from '@/api/rest/resources/types/conversation';
import { useOrganizationConversations } from '@/components/CoPilot/hooks/useOrganizationConversations';
import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { isNullish } from '@/utils/booleans';
import { printDDMMYYYYHHMM } from '@/utils/formatting/date';

import { CoPilotContextProvider } from '../CoPilotContextProvider';
import { useOrganizationAgents } from '../hooks/useAgents';
import { ConversationTitle, CurrentChat } from './Chat';
import { CoPilotHeader, CoPilotProps } from './CoPilot';
import { ACTION_BUTTON_STYLE } from './PromptedActions';

export const AIAgent = (props: CoPilotProps) => {
  const { t } = useTranslation();
  // Note: Uncomment to test each agent seperately
  // const [activeAgentType, setActiveAgentType] = useState<string | null>(null);
  const [activeConversation, setActiveConversation] = useState<string | null>(null);
  const [showConversations, setShowConversations] = useState<boolean>(false);
  const [chatKey, setChatKey] = useState<number>(2);

  const resetState = () => {
    // Note: Uncomment to test each agent seperately
    // setActiveAgentType(null);
    setActiveConversation(null);
    setShowConversations(false);
  };
  // Reset active agent whenever the sidebar is collapsed
  useEffect(() => {
    if (props.isCollapsed) {
      resetState();
    }
  }, [props.isCollapsed]);

  return (
    <CoPilotContextProvider value={{}}>
      <CoPilotHeader {...props} logoProps={{ showText: false }} className='flex-row gap-2'>
        {!props.isCollapsed ? (
          <Stack className='flex-1 flex-row justify-between items-center h-full'>
            <Stack className='flex-row gap-2 items-center'>
              <Button
                data-testid='back-button-chat-name'
                title={t('global.copilot.conversation.conversationHistory')}
                variant='outline'
                className='justify-start border-none typography-body1 text-text-primary rounded-md max-w-[150px] hover:bg-neutral-black-4 pl-2'
                onClick={() => setShowConversations(!showConversations)}
                leftAdornment={<Icon icon={showConversations ? '' : 'ri-arrow-left-line'} />}
              >
                {isNullish(activeConversation) ? (
                  t('global.copilot.conversation.newChat')
                ) : (
                  <ConversationTitle conversationId={activeConversation} showCreatedBy={false} />
                )}
              </Button>
            </Stack>
            <NewConversation
              onClick={() => {
                resetState();
                setChatKey((k) => k + 1);
              }}
            />
          </Stack>
        ) : null}
      </CoPilotHeader>
      {!props.isCollapsed && (
        <>
          {showConversations && (
            <Stack className='flex-1 overflow-y-auto hide-scrollbar'>
              <PreviousConversations
                setActiveConversation={(conversationId) => {
                  setChatKey((k) => k - 1);
                  setActiveConversation(conversationId);
                }}
                setShowConversations={setShowConversations}
              />
            </Stack>
          )}
          {!showConversations && (
            <Stack
              data-testid='copilot-agent'
              className='flex-1 overflow-hidden hide-scrollbar flex flex-col p-6 h-[calc(91%)] min-h-0'
            >
              {/* Note: uncomment to test each agent seperately */}
              {/* {isNullish(activeAgentType) && isNullish(activeConversation) && (
                  <AvailableAgents setAgentType={setActiveAgentType} />
                )} */}
              {(!isNullish(activeConversation) || chatKey) && (
                <CurrentChat
                  key={`${chatKey}-${activeConversation}`}
                  activeAgentType={AgentType.copilot}
                  conversationIdArg={activeConversation}
                />
              )}
            </Stack>
          )}
        </>
      )}
    </CoPilotContextProvider>
  );
};

export const NewConversation = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Button
      title={t('global.copilot.conversation.newChat')}
      data-testid='new-chat'
      variant='outline'
      className='border-none typography-button1 text-text-primary rounded-full w-8 hover:bg-primary-30 aspect-auto'
      onClick={onClick}
      leftAdornment={<Icon icon='ri-add-large-line' />}
    />
  );
};

export const ConversationPreview = ({
  conversation,
  onClick,
  isSelected = false,
}: {
  conversation: Conversation;
  onClick: () => void;
  isSelected?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Button
      key={conversation.id}
      variant='outline'
      className={cn(
        ACTION_BUTTON_STYLE,
        'bg-white-100 p-3 rounded-none flex flex-col w-full gap-2 justify-start',
        isSelected ? 'bg-primary-12' : '',
      )}
      onClick={onClick}
    >
      <span className='w-full truncate text-left font-semibold typography-caption text-text-primary'>
        {conversation.title || t('global.copilot.conversation.untitled')}
      </span>
      <Stack className='w-full flex-row justify-between items-center gap-4'>
        <span className='typography-caption text-text-secondary'>{conversation.created_by}</span>
        <span className='typography-caption text-text-secondary'>
          {printDDMMYYYYHHMM(conversation.modified_at) ?? ''}
        </span>
      </Stack>
    </Button>
  );
};

const PreviousConversations = withSuspenseBoundary(
  ({
    setActiveConversation,
    setShowConversations,
  }: {
    setActiveConversation: (conversationId: string) => void;
    setShowConversations: (show: boolean) => void;
  }) => {
    const { t } = useTranslation();
    const conversations = useOrganizationConversations().data;

    return (
      <Stack>
        {conversations.map((conversation) => (
          <ConversationPreview
            key={conversation.id}
            conversation={conversation}
            onClick={() => {
              setActiveConversation(conversation.id);
              setShowConversations(false);
            }}
          />
        ))}
        {conversations.length === 0 && (
          <span className='typography-body1 text-text-secondary text-center mt-4'>
            {t('global.copilot.conversation.noConversationHistory')}
          </span>
        )}
      </Stack>
    );
  },
  <Stack className='justify-center items-center p-4'>
    <Spinner size={4} />
  </Stack>,
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
const AvailableAgents = withSuspenseBoundary(
  ({ setAgentType }: { setAgentType: (agentType: string) => void }) => {
    const { t } = useTranslation();
    const agents = useOrganizationAgents().data;

    return (
      <Stack className='gap-6 justify-center items-center'>
        <span className='typography-body1 font-semibold text-primary-100 whitespace-pre-wrap'>
          {t('global.copilot.agent.prompt')}
        </span>
        {agents.length > 0 && (
          <Stack className='gap-4 justify-center items-center text-left'>
            {agents.map((agent, index) => (
              <Button
                key={`agent-${agent.agent_type}-${index}`}
                onClick={() => setAgentType(agent.agent_type)}
                variant='outline'
                className={ACTION_BUTTON_STYLE}
              >
                {agent.agent_type.replaceAll('_', ' ')}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>
    );
  },
  <Stack className='justify-center items-center p-4'>
    <Spinner size={4} />
  </Stack>,
);
